export type ClientErrorSource = 'window-error' | 'unhandled-rejection' | 'error-boundary' | 'support-chat';

/**
 * Fire-and-forget report of a client-side error to the audit log. Never
 * throws — a failure to report an error must not itself surface as a new
 * error. Uses log_client_error (see 20260717110000) when a session exists;
 * falls back to the anon-callable log_public_client_error (20260804010000)
 * on pre-login pages (/welcome, /login) where there is no session to
 * attribute the error to.
 */
export function reportClientError(
  message: string,
  options: { stack?: string; source?: ClientErrorSource; context?: Record<string, unknown> } = {}
): void {
  if (!message) return;

  const params = {
    p_message: message,
    p_stack: options.stack ?? null,
    p_url: typeof window !== 'undefined' ? window.location.href : null,
    p_source: options.source ?? 'unknown',
    p_context: options.context ?? null,
  };

  // The Supabase client is imported lazily: reportClientError is reachable from
  // the eagerly-mounted ErrorBoundary, and a static import would pull the whole
  // Supabase bundle into the entry chunk just to have an error reporter on
  // standby. Error reporting is fire-and-forget and never on the first-paint
  // path, so paying the import cost only when something actually breaks is fine.
  void (async () => {
    try {
      const { supabase } = await import('./supabase');
      const { data } = await supabase.auth.getSession();
      const rpc = data.session ? 'log_client_error' : 'log_public_client_error';
      const { error } = await supabase.rpc(rpc, params);
      if (error) {
        console.error('Failed to report client error to audit log:', error);
      }
    } catch (err) {
      console.error('Failed to report client error to audit log:', err);
    }
  })();
}
